import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiResponse, PageResponse, PageableDto } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import {
  mapMutationMessage,
  mapPageableResponse,
} from '../../utils/map-pageable';

export type GeneralCatalogStatus = 'active' | 'inactive';

/** Padre (`general_catalogs`) o ítem (`general_catalog_lists`). */
export interface GeneralCatalog {
  id: string;
  prefix: string;
  code: string;
  name: string;
  status: GeneralCatalogStatus;
}

export type GeneralCatalogWritePayload = {
  /** En el alta de un catálogo padre la API lo genera desde el nombre. */
  prefix?: string;
  code?: string;
  name: string;
  status?: string;
};

export type GeneralCatalogListParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  search?: string;
  status?: string;
};

/**
 * CRUD unificado de catálogos generales.
 *   GET/POST/PUT/DELETE/PATCH `/settings/general-catalogs`
 *   GET/POST/PUT/DELETE/PATCH `/settings/general-catalogs/{catalogId}/lists`
 */
@Injectable({ providedIn: 'root' })
export class GeneralCatalogService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get baseUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/general-catalogs`;
  }

  listCatalogs(
    params: GeneralCatalogListParams = {},
  ): Observable<PageResponse<GeneralCatalog>> {
    return this.http
      .get<ApiResponse<PageableDto>>(this.baseUrl, {
        params: this.toParams(params),
      })
      .pipe(
        map((res) =>
          mapPageableResponse(res, (item) => this.mapItem(item), params.size ?? 20),
        ),
      );
  }

  getCatalog(id: string): Observable<GeneralCatalog> {
    return this.http
      .get<ApiResponse<unknown>>(`${this.baseUrl}/${id}`)
      .pipe(map((res) => this.mapItem(res.data)));
  }

  createCatalog(
    payload: GeneralCatalogWritePayload,
  ): Observable<{ message: string }> {
    return this.http
      .post<{ success?: boolean; message?: string }>(this.baseUrl, payload)
      .pipe(map((res) => mapMutationMessage(res, 'Catálogo creado exitosamente')));
  }

  updateCatalog(
    id: string,
    payload: GeneralCatalogWritePayload,
  ): Observable<{ message: string }> {
    return this.http
      .put<{ success?: boolean; message?: string }>(
        `${this.baseUrl}/${id}`,
        payload,
      )
      .pipe(
        map((res) => mapMutationMessage(res, 'Catálogo actualizado exitosamente')),
      );
  }

  deleteCatalog(id: string): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(`${this.baseUrl}/${id}`)
      .pipe(
        map((res) => mapMutationMessage(res, 'Catálogo eliminado exitosamente')),
      );
  }

  /** Toggle ACTIVE/INACTIVE (sin body). */
  toggleCatalogStatus(id: string): Observable<{ message: string }> {
    return this.http
      .patch<{ success?: boolean; message?: string }>(
        `${this.baseUrl}/${id}/status`,
        {},
      )
      .pipe(
        map((res) => mapMutationMessage(res, 'Estatus actualizado exitosamente')),
      );
  }

  listItems(
    catalogId: string,
    params: GeneralCatalogListParams = {},
  ): Observable<PageResponse<GeneralCatalog>> {
    return this.http
      .get<ApiResponse<PageableDto>>(`${this.baseUrl}/${catalogId}/lists`, {
        params: this.toParams(params),
      })
      .pipe(
        map((res) =>
          mapPageableResponse(res, (item) => this.mapItem(item), params.size ?? 20),
        ),
      );
  }

  getItem(catalogId: string, id: string): Observable<GeneralCatalog> {
    return this.http
      .get<ApiResponse<unknown>>(`${this.baseUrl}/${catalogId}/lists/${id}`)
      .pipe(map((res) => this.mapItem(res.data)));
  }

  createItem(
    catalogId: string,
    payload: GeneralCatalogWritePayload,
  ): Observable<{ message: string }> {
    return this.http
      .post<{ success?: boolean; message?: string }>(
        `${this.baseUrl}/${catalogId}/lists`,
        payload,
      )
      .pipe(map((res) => mapMutationMessage(res, 'Ítem creado exitosamente')));
  }

  updateItem(
    catalogId: string,
    id: string,
    payload: GeneralCatalogWritePayload,
  ): Observable<{ message: string }> {
    return this.http
      .put<{ success?: boolean; message?: string }>(
        `${this.baseUrl}/${catalogId}/lists/${id}`,
        payload,
      )
      .pipe(map((res) => mapMutationMessage(res, 'Ítem actualizado exitosamente')));
  }

  deleteItem(catalogId: string, id: string): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        `${this.baseUrl}/${catalogId}/lists/${id}`,
      )
      .pipe(map((res) => mapMutationMessage(res, 'Ítem eliminado exitosamente')));
  }

  toggleItemStatus(
    catalogId: string,
    id: string,
  ): Observable<{ message: string }> {
    return this.http
      .patch<{ success?: boolean; message?: string }>(
        `${this.baseUrl}/${catalogId}/lists/${id}/status`,
        {},
      )
      .pipe(
        map((res) => mapMutationMessage(res, 'Estatus actualizado exitosamente')),
      );
  }

  private toParams(params: GeneralCatalogListParams): HttpParams {
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 20))
      .set('sort', params.sort ?? 'createdAt')
      .set('direction', params.direction ?? 'desc');

    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }
    if (params.status?.trim()) {
      httpParams = httpParams.set('status', params.status.trim());
    }
    return httpParams;
  }

  private mapItem(raw: unknown): GeneralCatalog {
    const item = (raw ?? {}) as Record<string, unknown>;
    return {
      id: String(item['id'] ?? ''),
      prefix: String(item['prefix'] ?? ''),
      code: String(item['code'] ?? ''),
      name: String(item['name'] ?? ''),
      status: this.normalizeStatus(item['status']),
    };
  }

  private normalizeStatus(status: unknown): GeneralCatalogStatus {
    const value = String(status ?? 'active').toLowerCase();
    return value === 'inactive' ? 'inactive' : 'active';
  }
}
